import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, FONTS } from '../../constants/theme.js';
import { fetchGuests, fetchGuest, createGuest, updateGuest, fetchGuestHistory, blacklistGuest, PAGE_SIZE,
  fetchCorporateAccounts } from '../../services/api.js';
import { Card, Btn, Modal, Field, Input, Select, Badge, Avatar, Table, TR, TD, fmtDate, fmtCurrency, SectionHeader, SearchInput, Paginator, Empty, Toast, Spinner, Tabs, OfflineBadge, useOfflineInvalidation } from '../../components/ui.jsx';

const ID_TYPES = ['passport','national_id','drivers_license','other'];
const NATIONALITIES = ['Rwandan','Kenyan','Ugandan','Tanzanian','Congolese','American','British','French','German','Chinese','Indian','Japanese','South African','Nigerian','Egyptian','Australian','Canadian','Other'];
const VIP_LEVELS = ['','silver','gold','platinum','diamond'];
const MARKET_SEGMENTS = ['','leisure','corporate','government','group','travel_agent','online','walk_in','other'];
const LANGUAGES = ['','English','French','Kinyarwanda','Swahili','Luganda','Arabic','Chinese','Spanish','German','Portuguese','Other'];

const emptyGuest = {
  firstName:'', lastName:'', email:'', phone:'', nationality:'',
  idType:'passport', idNumber:'', dateOfBirth:'',
  address:'', city:'', country:'', postalCode:'',
  vipLevel:'', preferences:'', allergies:'', notes:'',
  company:'', corporateAccountId:'', marketSegment:'', language:'', loyaltyNumber:'',
};

function SectionLabel({ label }) {
  return (
    <div style={{ gridColumn:'1/-1', borderBottom:`1px solid #E2E8F0`, paddingBottom:6, marginTop:8, marginBottom:4 }}>
      <p style={{ fontSize:10, fontWeight:700, color:'#64748B', textTransform:'uppercase', letterSpacing:.8, margin:0 }}>{label}</p>
    </div>
  );
}

function GuestForm({ data, onChange, corporateAccounts = [] }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
      <SectionLabel label="Personal Information" />
      <Field label="First Name" required><Input value={data.firstName||''} onChange={v => onChange('firstName',v)} placeholder="First name" /></Field>
      <Field label="Last Name"  required><Input value={data.lastName||''}  onChange={v => onChange('lastName',v)}  placeholder="Last name" /></Field>
      <Field label="Date of Birth"><Input type="date" value={data.dateOfBirth||''} onChange={v => onChange('dateOfBirth',v)} /></Field>
      <Field label="Nationality">
        <Select value={data.nationality||''} onChange={v => onChange('nationality',v)}>
          <option value="">— Select —</option>
          {NATIONALITIES.map(n => <option key={n} value={n}>{n}</option>)}
        </Select>
      </Field>
      <Field label="Preferred Language">
        <Select value={data.language||''} onChange={v => onChange('language',v)}>
          <option value="">— Any —</option>
          {LANGUAGES.filter(Boolean).map(l => <option key={l} value={l}>{l}</option>)}
        </Select>
      </Field>
      <Field label="VIP Level">
        <Select value={data.vipLevel||''} onChange={v => onChange('vipLevel',v)}>
          {VIP_LEVELS.map(v => <option key={v} value={v}>{v ? `${v.charAt(0).toUpperCase()+v.slice(1)} ⭐` : '— None —'}</option>)}
        </Select>
      </Field>

      <SectionLabel label="Contact" />
      <Field label="Email"><Input type="email" value={data.email||''} onChange={v => onChange('email',v)} placeholder="guest@email.com" /></Field>
      <Field label="Phone"><Input value={data.phone||''} onChange={v => onChange('phone',v)} placeholder="+250 7xx xxx xxx" /></Field>
      <div style={{ gridColumn:'1/-1' }}>
        <Field label="Address"><Input value={data.address||''} onChange={v => onChange('address',v)} placeholder="Street address" /></Field>
      </div>
      <Field label="City"><Input value={data.city||''} onChange={v => onChange('city',v)} /></Field>
      <Field label="Country"><Input value={data.country||''} onChange={v => onChange('country',v)} /></Field>

      <SectionLabel label="Identification" />
      <Field label="ID Type">
        <Select value={data.idType||'passport'} onChange={v => onChange('idType',v)}>
          {ID_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
        </Select>
      </Field>
      <Field label="ID Number"><Input value={data.idNumber||''} onChange={v => onChange('idNumber',v)} placeholder="Document number" /></Field>

      <SectionLabel label="Business & Loyalty" />
      <Field label="Company / Organization"><Input value={data.company||''} onChange={v => onChange('company',v)} placeholder="Company name" /></Field>
      <Field label="Linked Corporate Account" hint="Association only — does NOT auto-apply corporate pricing. Pricing only applies when staff explicitly create a Corporate Reservation for this guest.">
        <Select value={data.corporateAccountId||''} onChange={v => onChange('corporateAccountId', v)}>
          <option value="">— None —</option>
          {corporateAccounts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
      </Field>
      <Field label="Market Segment">
        <Select value={data.marketSegment||''} onChange={v => onChange('marketSegment',v)}>
          <option value="">— Select —</option>
          {MARKET_SEGMENTS.filter(Boolean).map(s => <option key={s} value={s}>{s.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}</option>)}
        </Select>
      </Field>
      <Field label="Loyalty / Member No."><Input value={data.loyaltyNumber||''} onChange={v => onChange('loyaltyNumber',v)} placeholder="Loyalty program number" /></Field>

      <SectionLabel label="Preferences & Notes" />
      <div style={{ gridColumn:'1/-1' }}>
        <Field label="Room & Service Preferences">
          <textarea value={data.preferences||''} onChange={e => onChange('preferences',e.target.value)}
            placeholder="e.g. High floor, extra pillows, quiet room, no feathers..."
            rows={2} style={{ width:'100%', border:`1.5px solid #E2E8F0`, borderRadius:10, padding:'10px 13px', fontSize:13, fontFamily:'inherit', resize:'vertical', outline:'none', boxSizing:'border-box', lineHeight:1.5 }} />
        </Field>
      </div>
      <div style={{ gridColumn:'1/-1' }}>
        <Field label="Food Allergies / Dietary Requirements">
          <Input value={data.allergies||''} onChange={v => onChange('allergies',v)} placeholder="e.g. Peanuts, shellfish, vegetarian, halal..." />
        </Field>
      </div>
      <div style={{ gridColumn:'1/-1' }}>
        <Field label="Internal Staff Notes (not shared with guest)">
          <textarea value={data.notes||''} onChange={e => onChange('notes',e.target.value)}
            placeholder="Internal notes visible to staff only..."
            rows={2} style={{ width:'100%', border:`1.5px solid #E2E8F0`, borderRadius:10, padding:'10px 13px', fontSize:13, fontFamily:'inherit', resize:'vertical', outline:'none', boxSizing:'border-box', lineHeight:1.5 }} />
        </Field>
      </div>
    </div>
  );
}

function VIPBadge({ level }) {
  if (!level) return null;
  const colors = { silver:'#A0A0A0', gold:T.gold, platinum:'#5B8DB8', diamond:'#9B59B6' };
  return <Badge label={level.toUpperCase()} color={colors[level] || T.muted} />;
}

export default function GuestManagement({ property, onNavigate }) {
  const [guests,    setGuests]   = useState([]);
  const [total,     setTotal]    = useState(0);
  const [page,      setPage]     = useState(0);
  const [search,    setSearch]   = useState('');
  const [loading,   setLoading]  = useState(true);
  const [toast,     setToast]    = useState('');
  const [saving,    setSaving]   = useState(false);

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [detailGuest, setDetailGuest] = useState(null);
  const [detailTab,   setDetailTab]   = useState('info');
  const [guestHistory, setGuestHistory] = useState([]);
  const [histLoading,  setHistLoading]  = useState(false);

  const [form, setForm] = useState({ ...emptyGuest });
  const [editMode, setEditMode] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [addingQ, setAddingQ] = useState(false);

  const debRef = useRef(null);
  const pid = property?.id;
  const currency = property?.currency || 'USD';

  const [corporateAccounts, setCorporateAccounts] = useState([]);
  useEffect(() => {
    if (!pid) return;
    fetchCorporateAccounts(pid, true).then(d => setCorporateAccounts(Array.isArray(d) ? d : [])).catch(() => {});
  }, [pid]);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const load = useCallback((s = search, p = page) => {
    if (!pid) return;
    setLoading(true);
    fetchGuests(pid, s, p, PAGE_SIZE)
      .then(data => {
        setGuests(Array.isArray(data) ? data : (data?.content ?? []));
        setTotal(data?.totalElements ?? (Array.isArray(data) ? data.length : 0));
      })
      .catch(() => setGuests([]))
      .finally(() => setLoading(false));
  }, [pid, search, page]);

  useOfflineInvalidation('guests', load);

  useEffect(() => { load(); }, [load]);

  const handleSearch = v => {
    setSearch(v); setPage(0);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => load(v, 0), 400);
  };

  const openDetail = async (guest) => {
    setDetailGuest(guest);
    setDetailTab('info');
    setEditMode(false);
    setQuestions([]);
    setNewQuestion('');
    setHistLoading(true);
    fetchGuestHistory(guest.id)
      .then(h => setGuestHistory(Array.isArray(h) ? h : []))
      .catch(() => setGuestHistory([]))
      .finally(() => setHistLoading(false));
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.trim() || !detailGuest?.id) return;
    setAddingQ(true);
    const entry = {
      id: Date.now(),
      text: newQuestion.trim(),
      createdAt: new Date().toLocaleString('en-GB'),
      resolved: false,
    };
    const updatedQ = [...questions, entry];
    setQuestions(updatedQ);
    // Append to notes field in backend
    const updatedNotes = [detailGuest.notes, `[Q ${entry.createdAt}] ${entry.text}`].filter(Boolean).join('\n');
    try {
      await updateGuest(detailGuest.id, { ...detailGuest, notes: updatedNotes });
      setDetailGuest(p => ({ ...p, notes: updatedNotes }));
      showToast('✅ Question added');
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setAddingQ(false); setNewQuestion(''); }
  };

  const handleCreate = async () => {
    if (!form.firstName || !form.lastName) { showToast('First and last name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, propertyId: pid, fullName: `${form.firstName} ${form.lastName}` };
      const r = await createGuest(payload);
      showToast(r?._offline ? '⏳ Guest saved offline — will sync when server is back' : '✅ Guest profile created');
      setCreateOpen(false);
      setForm({ ...emptyGuest });
      load();
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!detailGuest?.id) return;
    setSaving(true);
    try {
      const r = await updateGuest(detailGuest.id, { ...detailGuest });
      showToast(r?._offline ? '⏳ Changes saved offline — will sync when server is back' : '✅ Guest updated');
      if (!r?._offline) setDetailGuest(r);
      setEditMode(false);
      load();
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleBlacklist = async (guest) => {
    const reason = window.prompt('Reason for blacklisting this guest:');
    if (!reason) return;
    try {
      await blacklistGuest(guest.id, reason);
      showToast('Guest blacklisted');
      setDetailGuest(null);
      load();
    } catch (e) { showToast(`❌ ${e.message}`); }
  };

  const DETAIL_TABS = [
    { id:'info',      label:'Profile',      icon:'👤' },
    { id:'history',   label:'Stay History', icon:'📅', count: guestHistory.length },
    { id:'prefs',     label:'Preferences',  icon:'⭐' },
    { id:'questions', label:'Questions',    icon:'❓', count: questions.length || undefined },
  ];

  return (
    <div className="fade-up">
      <SectionHeader
        title="Guest Management"
        subtitle={`${total} guest profiles`}
        right={<>
          <Btn variant="primary" onClick={() => { setForm({...emptyGuest}); setCreateOpen(true); }}>+ New Guest</Btn>
        </>}
      />

      {/* Search */}
      <div style={{ marginBottom:16, maxWidth:400 }}>
        <SearchInput value={search} onChange={handleSearch} placeholder="Search guests by name, email, phone..." />
      </div>

      {/* Table */}
      <Card style={{ padding:0, overflow:'hidden' }}>
        <Table headers={['Guest','Contact','Nationality','ID','VIP','Visits','Last Stay','']}>
          {loading ? (
            <tr><td colSpan={8} style={{ textAlign:'center', padding:40 }}><Spinner /></td></tr>
          ) : guests.length === 0 ? (
            <tr><td colSpan={8}><Empty icon="👥" title="No guests found" subtitle="Create a guest profile or search differently" /></td></tr>
          ) : guests.map(g => (
            <TR key={g.id} onClick={() => openDetail(g)}>
              <TD>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <Avatar name={g.fullName || `${g.firstName} ${g.lastName}`} size={34} color={g.vipLevel ? T.gold : T.teal} />
                  <div>
                    <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>{g.fullName || `${g.firstName} ${g.lastName}`}</p>
                    {g.blacklisted && <Badge label="Blacklisted" color={T.danger} />}
                  </div>
                </div>
              </TD>
              <TD><div style={{ fontSize:12, color:T.muted }}><div>{g.email || '—'}</div><div>{g.phone || '—'}</div></div></TD>
              <TD><span style={{ fontSize:12, color:T.slate }}>{g.nationality || '—'}</span></TD>
              <TD><span style={{ fontSize:12, color:T.muted, fontFamily:FONTS.mono }}>{g.idNumber || '—'}</span></TD>
              <TD><VIPBadge level={g.vipLevel} /></TD>
              <TD><span style={{ fontSize:13, fontWeight:700, color:T.navy }}>{g.visitCount || 0}</span></TD>
              <TD><span style={{ fontSize:12, color:T.muted }}>{g.lastStayDate ? fmtDate(g.lastStayDate) : '—'}</span></TD>
              <TD><Btn size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openDetail(g); }}>View</Btn></TD>
            </TR>
          ))}
        </Table>
        <div style={{ padding:'12px 18px', borderTop:`1px solid ${T.border}` }}>
          <Paginator page={page} total={total} size={PAGE_SIZE} onChange={p => { setPage(p); load(search, p); }} />
        </div>
      </Card>

      {/* Create Guest Modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="+ New Guest Profile" width={680}>
        <GuestForm data={form} onChange={(k, v) => setForm(p => ({...p, [k]:v}))} corporateAccounts={corporateAccounts} />
        <div style={{ display:'flex', gap:10, marginTop:16 }}>
          <Btn variant="secondary" onClick={() => setCreateOpen(false)} style={{ flex:1 }}>Cancel</Btn>
          <Btn variant="primary" disabled={saving} onClick={handleCreate} style={{ flex:2 }}>{saving ? 'Saving…' : 'Create Guest'}</Btn>
        </div>
      </Modal>

      {/* Guest Detail Modal */}
      <Modal open={!!detailGuest} onClose={() => { setDetailGuest(null); setEditMode(false); }} title="Guest Profile" width={720}>
        {detailGuest && (
          <>
            {/* Header */}
            <div style={{ display:'flex', alignItems:'center', padding:'0 0 16px',
              borderBottom:`1px solid ${T.border}`, marginBottom:16, flexWrap:'wrap', gap:14 }}>
              <div style={{ position:'relative', flexShrink:0 }}>
                <Avatar name={detailGuest.fullName || `${detailGuest.firstName} ${detailGuest.lastName}`} size={60} color={detailGuest.vipLevel ? T.gold : T.teal} />
                {detailGuest.vipLevel && (
                  <div style={{ position:'absolute', bottom:-2, right:-2, width:20, height:20, borderRadius:'50%',
                    background:T.gold, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10 }}>⭐</div>
                )}
              </div>
              <div style={{ flex:1, minWidth:200 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <p style={{ fontSize:18, fontWeight:700, color:T.navy, margin:0 }}>
                    {detailGuest.fullName || `${detailGuest.firstName} ${detailGuest.lastName}`}
                  </p>
                  <VIPBadge level={detailGuest.vipLevel} />
                  {detailGuest.blacklisted && <Badge label="Blacklisted" color={T.danger} />}
                </div>
                <div style={{ display:'flex', gap:12, marginTop:4, flexWrap:'wrap' }}>
                  {detailGuest.email && <span style={{ fontSize:12, color:T.muted }}>📧 {detailGuest.email}</span>}
                  {detailGuest.phone && <span style={{ fontSize:12, color:T.muted }}>📞 {detailGuest.phone}</span>}
                </div>
                <div style={{ display:'flex', gap:12, marginTop:3, flexWrap:'wrap' }}>
                  {detailGuest.nationality && <span style={{ fontSize:11, color:T.muted }}>🌍 {detailGuest.nationality}</span>}
                  <span style={{ fontSize:11, color:T.muted }}>🏨 {detailGuest.visitCount || 0} stay{detailGuest.visitCount !== 1 ? 's' : ''}</span>
                  {detailGuest.lastStayDate && <span style={{ fontSize:11, color:T.muted }}>🗓️ Last: {fmtDate(detailGuest.lastStayDate)}</span>}
                  {detailGuest.company && <span style={{ fontSize:11, color:T.muted }}>💼 {detailGuest.company}</span>}
                </div>
              </div>
              <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                {!editMode ? (
                  <>
                    <Btn size="sm" variant="ghost" onClick={() => setEditMode(true)}>✏️ Edit</Btn>
                    {!detailGuest.blacklisted && (
                      <Btn size="sm" variant="secondary" onClick={() => handleBlacklist(detailGuest)}>🚫 Blacklist</Btn>
                    )}
                  </>
                ) : (
                  <>
                    <Btn size="sm" variant="secondary" onClick={() => setEditMode(false)}>Cancel</Btn>
                    <Btn size="sm" variant="primary" disabled={saving} onClick={handleUpdate}>{saving ? '⏳ Saving…' : '💾 Save'}</Btn>
                  </>
                )}
              </div>
            </div>

            <Tabs tabs={DETAIL_TABS} active={detailTab} onSelect={setDetailTab} />

            {detailTab === 'info' && (
              editMode ? (
                <GuestForm data={detailGuest} onChange={(k, v) => setDetailGuest(p => ({...p, [k]:v}))} corporateAccounts={corporateAccounts} />
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {[
                    ['👤 Full Name',     detailGuest.fullName || `${detailGuest.firstName} ${detailGuest.lastName}`],
                    ['📧 Email',         detailGuest.email || '—'],
                    ['📞 Phone',         detailGuest.phone || '—'],
                    ['🎂 Date of Birth', detailGuest.dateOfBirth ? fmtDate(detailGuest.dateOfBirth) : '—'],
                    ['🌍 Nationality',   detailGuest.nationality || '—'],
                    ['🌐 Language',      detailGuest.language || '—'],
                    [`🪪 ${(detailGuest.idType||'ID').replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}`, detailGuest.idNumber || '—'],
                    ['💼 Company',       detailGuest.company || '—'],
                    ['📊 Market Segment', detailGuest.marketSegment?.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()) || '—'],
                    ['🎖️ Loyalty No.',   detailGuest.loyaltyNumber || '—'],
                    ['📍 Address',       [detailGuest.address, detailGuest.city, detailGuest.country].filter(Boolean).join(', ') || '—'],
                    ['🏨 Total Stays',   detailGuest.visitCount || 0],
                  ].map(([k, v]) => (
                    <div key={k} style={{ background:T.surface, borderRadius:10, padding:'10px 13px', border:`1px solid ${T.border}` }}>
                      <p style={{ fontSize:9, fontWeight:700, color:T.muted, textTransform:'uppercase', letterSpacing:.7, marginBottom:4 }}>{k}</p>
                      <p style={{ fontSize:13, color:T.navy, margin:0, fontWeight:v !== '—' ? 600 : 400 }}>{v}</p>
                    </div>
                  ))}
                </div>
              )
            )}

            {detailTab === 'history' && (
              histLoading ? <div style={{ textAlign:'center', padding:40 }}><Spinner /></div> :
              guestHistory.length === 0 ? <Empty icon="📅" title="No stay history" /> :
              <Table headers={['Confirmation','Dates','Room','Amount','Status']}>
                {guestHistory.map(h => (
                  <TR key={h.id}>
                    <TD><span style={{ fontFamily:FONTS.mono, fontSize:11 }}>{h.confirmationNumber}</span></TD>
                    <TD><span style={{ fontSize:12, color:T.muted }}>{fmtDate(h.arrivalDate)} → {fmtDate(h.departureDate)}</span></TD>
                    <TD>{h.roomNumber || '—'}</TD>
                    <TD><span style={{ fontWeight:700, color:T.navy }}>{fmtCurrency(h.totalAmount, currency)}</span></TD>
                    <TD><Badge label={h.status} color={T.teal} /></TD>
                  </TR>
                ))}
              </Table>
            )}

            {detailTab === 'prefs' && (
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                {[
                  ['⭐ Room & Service Preferences', detailGuest.preferences],
                  ['⚠️ Food Allergies / Dietary',   detailGuest.allergies],
                  ['💼 Market Segment', detailGuest.marketSegment?.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())],
                  ['🌐 Preferred Language', detailGuest.language],
                  ['🏢 Company', detailGuest.company],
                  ['🎖️ Loyalty No.', detailGuest.loyaltyNumber],
                  ['📝 Internal Staff Notes', detailGuest.notes],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p style={{ fontSize:10, fontWeight:700, color:T.muted, textTransform:'uppercase', letterSpacing:.7, marginBottom:4 }}>{label}</p>
                    <p style={{ fontSize:13, color:value ? T.navy : T.muted, background:T.surface, borderRadius:8, padding:'10px 14px', minHeight:36, whiteSpace:'pre-wrap' }}>{value || '—'}</p>
                  </div>
                ))}
              </div>
            )}

            {detailTab === 'questions' && (
              <div>
                <div style={{ marginBottom:16, padding:'12px 14px', background:`${T.teal}10`, border:`1px solid ${T.teal}30`, borderRadius:12 }}>
                  <p style={{ fontSize:12, color:T.teal, margin:0, fontWeight:600 }}>
                    💡 Use this tab to record guest questions, inquiries, complaints or special requests during their stay.
                  </p>
                </div>

                {/* Add question */}
                <div style={{ marginBottom:16 }}>
                  <p style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:'uppercase', letterSpacing:.7, marginBottom:8 }}>New Question / Note</p>
                  <div style={{ display:'flex', gap:8 }}>
                    <textarea
                      value={newQuestion}
                      onChange={e => setNewQuestion(e.target.value)}
                      placeholder="Type a guest question, complaint, request, or note…"
                      rows={2}
                      style={{ flex:1, border:`1.5px solid ${T.border}`, borderRadius:10, padding:'10px 13px',
                        fontSize:13, fontFamily:'inherit', resize:'none', outline:'none', lineHeight:1.5 }}
                    />
                    <button onClick={handleAddQuestion} disabled={addingQ || !newQuestion.trim()} style={{
                      alignSelf:'flex-end', padding:'9px 18px', borderRadius:10, border:'none',
                      background: newQuestion.trim() ? T.teal : T.border, color:T.white,
                      fontSize:12, fontWeight:700, cursor: newQuestion.trim() ? 'pointer' : 'not-allowed',
                      fontFamily:'inherit', transition:'background .15s',
                    }}>
                      {addingQ ? '⏳' : '+ Add'}
                    </button>
                  </div>
                </div>

                {/* Questions list */}
                {questions.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'28px 0', color:T.muted }}>
                    <p style={{ fontSize:28, marginBottom:8 }}>❓</p>
                    <p style={{ fontSize:13 }}>No questions recorded yet</p>
                    <p style={{ fontSize:11, marginTop:4 }}>Add a question or note above</p>
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {questions.map((q, i) => (
                      <div key={q.id} style={{ display:'flex', gap:12, padding:'12px 14px',
                        background:q.resolved ? T.successPale : T.surface,
                        border:`1px solid ${q.resolved ? T.success+'40' : T.border}`,
                        borderRadius:10 }}>
                        <div style={{ width:30, height:30, borderRadius:'50%', background:`${T.teal}20`,
                          display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>
                          {q.resolved ? '✅' : '❓'}
                        </div>
                        <div style={{ flex:1 }}>
                          <p style={{ fontSize:13, color:T.navy, margin:0 }}>{q.text}</p>
                          <p style={{ fontSize:10, color:T.muted, margin:'4px 0 0' }}>{q.createdAt}</p>
                        </div>
                        <button onClick={() => setQuestions(qs => qs.map((x, j) => j===i ? {...x, resolved:!x.resolved} : x))}
                          style={{ background:'none', border:`1px solid ${T.border}`, borderRadius:7,
                            padding:'4px 10px', cursor:'pointer', fontSize:11, color:T.muted, fontFamily:'inherit',
                            alignSelf:'flex-start', flexShrink:0 }}>
                          {q.resolved ? 'Reopen' : 'Resolve'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal>

      <Toast message={toast} />
    </div>
  );
}